import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Chip, Alert } from '@mui/material';
import { CompareArrows as CompareIcon } from '@mui/icons-material';
// import { Print as PrintIcon, FileDownload as ExportIcon } from '@mui/icons-material';
// import { format } from 'date-fns';
// import { es } from 'date-fns/locale';
import {
  DashboardLocationProvider,
  useDashboardLocation,
} from '../../../contexts/DashboardLocationContext';
import { KPIBar } from '../../dashboard/KPIBar';
import { DashboardScopeTabs } from '../../dashboard/DashboardScopeTabs';
import { getRangeFromPreset } from '../../dashboard/DashboardDateRangePicker';
import type { DateRangeValue } from '../../dashboard/DashboardDateRangePicker';
import type { ChartType, StatisticsFilterState } from '../../../types/statistics.types';
import {
  CHARTS_WITH_COMPARISON,
  CHARTS_WITH_PARTIAL_COMPARISON,
} from '../../../types/statistics.types';
import { useColorMode } from '../../../context/ColorModeContext';
import { getPeriodLabel } from './statisticsComparisonUtils';
import StatisticsFilters from './StatisticsFilters';
import StatisticsChart from './StatisticsChart';
import { useStatisticsData } from './Statistics.hooks';
import { ListPageMain, ListPageFiltersSection } from '../../common/list-page';
// import { downloadStatisticsCsv } from './statisticsExportUtils';

const defaultDateRange = (): DateRangeValue => {
  const { startDate, endDate } = getRangeFromPreset('last7');
  return { startDate, endDate, presetId: 'last7' };
};

const StatisticsContent: React.FC = () => {
  const { mode: appColorMode } = useColorMode();
  const dashboardMode = appColorMode === 'dark' ? 'dark' : 'light';
  const { scope } = useDashboardLocation();
  const isGlobalScope = scope === 'global';
  const [filters, setFilters] = useState<StatisticsFilterState>({
    chartType: 'sales',
    dateRange: defaultDateRange(),
    scope: isGlobalScope ? 'global' : String(scope),
  });
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const { kpis, chartData, loading, refetch, comparisonMeta } = useStatisticsData(
    filters,
    isGlobalScope,
  );

  const handleFiltersChange = useCallback((newFilters: StatisticsFilterState) => {
    setFilters(newFilters);
  }, []);

  const handleSearch = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleReset = useCallback(() => {
    setFilters({
      chartType: 'sales',
      dateRange: defaultDateRange(),
      scope: isGlobalScope ? 'global' : String(scope),
      comparisonMode: 'none',
      basePeriod: undefined,
      comparePeriods: undefined,
      selectedProductIds: undefined,
    });
  }, [scope, isGlobalScope]);

  const handleToggleMoreFilters = useCallback(() => {
    setShowMoreFilters((prev) => !prev);
  }, []);

  const handleKpiSelect = useCallback((kpiId: string) => {
    if (
      [
        'sales',
        'orders',
        'units',
        'margin',
        'devoluciones',
        'top-products',
        'product-trend',
        'stock-ingress',
      ].includes(kpiId)
    ) {
      setFilters((prev) => ({ ...prev, chartType: kpiId as ChartType }));
    }
  }, []);

  const handleScopeChange = useCallback((newScope: 'global' | number) => {
    setFilters((prev) => ({
      ...prev,
      scope: newScope === 'global' ? 'global' : String(newScope),
    }));
  }, []);

  useEffect(() => {
    const nextScope = scope === 'global' ? 'global' : String(scope);
    setFilters((prev) => (prev.scope === nextScope ? prev : { ...prev, scope: nextScope }));
  }, [scope]);

  // TODO: Definir qué imprimir/exportar antes de reactivar
  // const handlePrint = useCallback(() => {
  //   window.print();
  // }, []);

  // const handleExport = useCallback(() => {
  //   const label =
  //     filters.dateRange.presetId === 'custom'
  //       ? `${format(filters.dateRange.startDate, 'd-MMM', { locale: es })}_${format(filters.dateRange.endDate, 'd-MMM-yyyy', { locale: es })}`
  //       : filters.dateRange.presetId;
  //   downloadStatisticsCsv(filters.chartType, chartData, label);
  // }, [filters.chartType, filters.dateRange, chartData]);

  const isComparisonActive =
    filters.comparisonMode === 'months' || filters.comparisonMode === 'years';
  const chartSupportsComparison =
    CHARTS_WITH_COMPARISON.includes(filters.chartType) ||
    CHARTS_WITH_PARTIAL_COMPARISON.includes(filters.chartType);
  const periodGranularity = filters.comparisonMode === 'months' ? 'months' : 'years';
  const comparisonLabel =
    comparisonMeta?.periodLabels?.join(' vs ') ??
    (filters.basePeriod && filters.comparePeriods?.length
      ? [
          getPeriodLabel(filters.basePeriod, periodGranularity),
          ...filters.comparePeriods.map((p) => getPeriodLabel(p, periodGranularity)),
        ].join(' vs ')
      : 'Comparando períodos');

  return (
    <ListPageMain sectionGap={1}>
      {isComparisonActive && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Chip
            icon={<CompareIcon sx={{ fontSize: 18 }} />}
            label={comparisonLabel}
            size="small"
            sx={{
              fontWeight: 500,
              bgcolor: 'action.selected',
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        </Box>
      )}

      <ListPageFiltersSection>
        <StatisticsFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          onReset={handleReset}
          showMoreFilters={showMoreFilters}
          onToggleMoreFilters={handleToggleMoreFilters}
          dashboardMode={dashboardMode}
        />
      </ListPageFiltersSection>

      <Box>
        <KPIBar
          kpis={kpis}
          loading={loading}
          mode={dashboardMode}
          columns={{ xs: 8, sm: 8, md: 8, lg: 8 }}
          selectedKpiId={filters.chartType}
          onKpiSelect={handleKpiSelect}
          kpisWithComparison={[
            'sales',
            'orders',
            'units',
            'margin',
            'devoluciones',
            'top-products',
            'product-trend',
            'stock-ingress',
          ]}
        />
        <DashboardScopeTabs mode={dashboardMode} onScopeChange={handleScopeChange} />
      </Box>

      {isComparisonActive && !chartSupportsComparison && (
        <Alert
          severity="info"
          sx={{ mt: 3, mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  comparisonMode: 'none',
                  basePeriod: undefined,
                  comparePeriods: undefined,
                }))
              }
            >
              Desactivar comparación
            </Button>
          }
        >
          Esta gráfica no soporta comparación de períodos. Selecciona Ventas en dinero, Número de
          ventas, Piezas, Margen, Devoluciones, Top productos o Stock ingresado para comparar.
        </Alert>
      )}
      <StatisticsChart
        chartType={filters.chartType}
        chartData={chartData}
        loading={loading}
        mode={dashboardMode}
        isGlobalScope={isGlobalScope}
        comparisonMeta={comparisonMeta}
      />
    </ListPageMain>
  );
};

const Statistics: React.FC = () => {
  return (
    <DashboardLocationProvider>
      <StatisticsContent />
    </DashboardLocationProvider>
  );
};

export default Statistics;
